import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { toast } from 'react-toastify';
import {
    FaUserClock,
    FaSync,
    FaUsers,
    FaUserCheck,
    FaUserTimes,
    FaDoorOpen,
    FaUserPlus,
    FaFlagCheckered,
} from 'react-icons/fa';
import { getAggregate } from '../../../_services/api/staffTurnover';
import { isAdminPanel, getTeamIdFromURL } from '../../../_helpers/methods';
import TeamSelectFilter from '../TeamSelectFilter';
import KpiCard from './KpiCard';
import TrendChart from './TrendChart';
import StaffTable from './StaffTable';
import { VscLoading } from 'react-icons/vsc';
import { Button, Form } from 'react-bootstrap';
import moment from 'moment';
import DateTimePicker from 'react-datetime-picker/dist/DateTimePicker';
import { css } from '@emotion/react';

const HEALTH_COLORS = {
    healthy: '#4caf50',
    concerning: '#ff9800',
    critical: '#f44336',
};

const getOneYearAgo = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date;
};

export default function StaffTurnoverDashboard() {
    const [aggregate, setAggregate] = useState(null);
    const teamIdFromURL = getTeamIdFromURL();
    const [selectedTeamOption, setSelectedTeamOption] = useState(() =>
        teamIdFromURL ? { label: teamIdFromURL, value: teamIdFromURL } : null
    );
    const [loadingAgg, setLoadingAgg] = useState(false);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
    const selectedTeam = selectedTeamOption?.value || '';

    const [filters, setFilters] = useState({
        startDate: getOneYearAgo().toISOString(),
        endDate: new Date().toISOString(),
    });

    const loadAggregate = useCallback(async (team, startDate, endDate) => {
        setLoadingAgg(true);
        try {
            const res = await getAggregate(team || getTeamIdFromURL() || undefined, startDate, endDate);
            setAggregate(res);
            if (res?.updatedAt) {
                setLastUpdatedAt(res?.updatedAt);
            }
        } catch (e) {
            toast.error(`Failed to load turnover KPIs: ${e.message}`);
        } finally {
            setLoadingAgg(false);
        }
    }, []);

    const handleDateChange = (type) => (date) => {
        if (!date) {
            setFilters((prev) => ({ ...prev, [type]: null }));
            return;
        }
        setFilters((prev) => ({ ...prev, [type]: date.toISOString() }));
    };

    useEffect(() => {
        loadAggregate(selectedTeam, filters.startDate, filters.endDate);
    }, [filters.startDate, filters.endDate, selectedTeam, loadAggregate]);

    const kpis = aggregate?.kpis;
    const healthColor = kpis ? HEALTH_COLORS[kpis.healthBand] : '#999';
    const turnoverDisplay = kpis ? `${kpis.turnoverPct}%` : '-';

    return (
        <div style={{ padding: 24 }} data-testid="staff-turnover-container">
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 0 }}>
                <FaUserClock /> Staff Turnover
            </h2>
            <span className="text-muted " style={{ fontSize: '12px' }}>
                {lastUpdatedAt ? (
                    <span> Last updated {moment(lastUpdatedAt).fromNow()}</span>
                ) : (
                    <span>Will be updated every 60 minutes</span>
                )}
            </span>
            <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
                Staff are <strong>active</strong> if they submitted any SOS, EOS, or Backup form within the last 14
                days. After 14 days with no submissions, staff are only counted as turnover if SOS/EOS forms are still
                being submitted in their client channel(s) — meaning services are ongoing but the staff left. If
                services also ended (no channel activity), staff are <strong>finished</strong> and excluded from
                turnover. New joins are identified by an SOS form submitted within the last 30 days. Managers and
                duplicate accounts are always excluded. Data shown covers the last <strong>90 days</strong> of SOS/EOS
                submissions.
            </p>

            <div style={{ marginBottom: 20, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
                {isAdminPanel() && (
                    <>
                        <Form.Group css={css``}>
                            <Form.Label style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>Team:</Form.Label>
                            <div>
                                <TeamSelectFilter
                                    selectedTeamOption={selectedTeamOption}
                                    setSelectedTeamOption={setSelectedTeamOption}
                                />
                            </div>
                        </Form.Group>
                    </>
                )}

                <Form.Group css={css``}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>From:</Form.Label>
                    <div>
                        <DateTimePicker
                            onChange={handleDateChange('startDate')}
                            value={filters.startDate ? new Date(filters.startDate) : null}
                            format="MM-dd-y"
                            disableClock={true}
                            calendarType={'US'}
                            maxDate={filters.endDate ? new Date(filters.endDate) : new Date()}
                        />
                    </div>
                </Form.Group>

                <Form.Group css={css``}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>To:</Form.Label>
                    <div>
                        <DateTimePicker
                            onChange={handleDateChange('endDate')}
                            value={filters.endDate ? new Date(filters.endDate) : null}
                            format="MM-dd-y"
                            disableClock={true}
                            calendarType={'US'}
                            maxDate={new Date()}
                            minDate={filters.startDate ? new Date(filters.startDate) : undefined}
                        />
                    </div>
                </Form.Group>
                <Form.Group css={css``}>
                    <Form.Label style={{ fontSize: 13, fontWeight: 600, color: '#555' }}></Form.Label>
                    <div>
                        <Button
                            onClick={() => {
                                loadAggregate(selectedTeam, filters.startDate, filters.endDate);
                                setRefreshTrigger((prev) => prev + 1);
                            }}
                            disabled={loadingAgg}
                            style={{
                                padding: '6px 14px',
                                color: '#fff',
                                border: 'none',
                                borderRadius: 4,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                            }}
                        >
                            <FaSync className={loadingAgg ? 'spin' : ''} /> Refresh
                        </Button>
                    </div>
                </Form.Group>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 'auto' }}></div>
            </div>

            {/* KPI cards */}
            {loadingAgg ? (
                <div style={{ padding: 16, color: '#999' }}>
                    <VscLoading className="mr-2 spin" />
                    Loading KPIs...
                </div>
            ) : (
                <Fragment>
                    {/* Turnover percentage banner */}
                    {kpis && (
                        <div
                            style={{
                                background:
                                    healthColor === '#4caf50'
                                        ? '#e8f5e9'
                                        : healthColor === '#ff9800'
                                          ? '#fff3e0'
                                          : '#ffebee',
                                border: `2px solid ${healthColor}`,
                                borderRadius: 8,
                                padding: 16,
                                marginBottom: 16,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#555' }}>Turnover Rate</div>
                                <div style={{ fontSize: 11, color: '#888' }}>
                                    {kpis.active + kpis.leaving + kpis.exited} in turnover pool | {kpis.finished || 0}{' '}
                                    finished | {kpis.managersExcluded || 0} managers | {kpis.duplicatesExcluded || 0}{' '}
                                    duplicates (excluded)
                                </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: 32, fontWeight: 700, color: healthColor }}>
                                    {turnoverDisplay}
                                </div>
                                <div style={{ fontSize: 11, color: '#888', textTransform: 'capitalize' }}>
                                    {kpis.healthBand} ({kpis.healthBands[kpis.healthBand]})
                                </div>
                            </div>
                        </div>
                    )}
                    {kpis && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
                            <KpiCard
                                icon={<FaUsers />}
                                label="EVV Staff"
                                value={kpis.totalNonManager || kpis.total}
                                sub={`${kpis.totalAllEmployees || 0} total`}
                            />
                            <KpiCard icon={<FaUserCheck />} label="Active (14d)" value={kpis.active} color="#4caf50" />
                            <KpiCard icon={<FaUserTimes />} label="Leaving" value={kpis.leaving} color="#ff9800" />
                            <KpiCard icon={<FaDoorOpen />} label="Exited (EOS)" value={kpis.exited} color="#f44336" />
                            <KpiCard
                                icon={<FaFlagCheckered />}
                                label="Finished"
                                value={kpis.finished || 0}
                                color="#9c27b0"
                            />
                            <KpiCard icon={<FaUserPlus />} label="New Joins" value={kpis.newJoins} color="#2196f3" />
                            <KpiCard icon={<FaUserClock />} label="Avg Tenure" value={`${kpis.avgTenure}d`} />
                        </div>
                    )}

                    {/* Trend chart */}
                    {aggregate?.trend && <TrendChart trend={aggregate.trend} />}
                </Fragment>
            )}

            {/* Staff table */}
            <StaffTable
                team={selectedTeam}
                sortBy="createdAt:desc"
                refreshTrigger={refreshTrigger}
                startDate={filters.startDate}
                endDate={filters.endDate}
            />
        </div>
    );
}
